import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { map, Observable, Subscription, tap } from 'rxjs';
import { promiseWithCatch, safelyDetectChanges, unsubscribeAll } from 'src/app/common/app.helpers';
import { AccountStatus, UserBasic } from 'src/app/swagger-api';
import { FilterOption } from 'src/app/views/filter-drop-down/filter-drop-down.component';
import { SortCategories } from 'src/app/views/sort-elements/sort-drop-down/sort-drop-down.helpers';
import ProjectLinks from '../../../../common/project.links';
import { ChatSendMessageToService } from '../../../chat/services/chat-send-message-to.service';
import { toSortCategories } from '../../../shared/list-controller/list-controller';
import { ConnectionRequest, FilterValue } from '../../relation-request.interface';
import { RELATION_REQUESTS_PAGE_CONFIG } from '../../relation-request.tokens';
import { UserRelationRequestService } from '../../services/relation-request.service';

type PageParams = {
  page: number;
  filtering: string;
  sorting: SortCategories;
};

type GroupMessagingForm = FormGroup<{
  selectAll: FormControl<boolean>;
  items: FormArray<FormControl<boolean>>;
}>;

@Component({
  selector: 'user-group-messaging',
  templateUrl: './user-group-messaging.component.html',
  styleUrls: ['./user-group-messaging.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelatedUserMessageComponent<T extends ConnectionRequest> implements OnInit, OnDestroy {
  public readonly LINK_CONNECTIONS_LIST: string = ProjectLinks.LINK_CONNECTIONS_LIST;
  public readonly pageConfig = inject(RELATION_REQUESTS_PAGE_CONFIG, { self: false });

  public selectedAll = false;
  public users: UserBasic[] = [];
  public form: GroupMessagingForm | null = null;
  public selectedFilter$: Observable<FilterOption<FilterValue> | null>;

  private subscriptions$: Subscription[] = [];

  private relationRequestService = inject(UserRelationRequestService<T>, { self: false });
  private chatSendMessageToService = inject(ChatSendMessageToService);
  private changeDetector = inject(ChangeDetectorRef);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  public ngOnInit(): void {
    this.selectedFilter$ = this.pageParams().pipe(
      map(
        ({ filtering }: PageParams) =>
          this.pageConfig.filter?.options.find((item: FilterOption<FilterValue>) => item.value === filtering) ?? null,
      ),
    );

    this.subscriptions$.push(
      this.relationRequestService.relatedUsersList$
        .pipe(
          tap((users: UserBasic[]) => {
            this.users = users;
            this.createForm();

            if (this.selectedAll) {
              this.selectedAll = false;
              this.form.reset();
              this.form.markAsDirty();
            }

            safelyDetectChanges(this.changeDetector);
          }),
        )
        .subscribe(),
    );
  }

  public applyFilterByCategory(option: FilterOption<FilterValue>): void {
    promiseWithCatch(
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path], {
        relativeTo: this.activatedRoute.parent,
        queryParams: { filtering: option.value },
      }),
    );
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public selectAll(): void {
    this.selectedAll = !this.selectedAll;

    this.usersCheckboxFormArray.forEach((control, index) => {
      if (this.users[index]?.account_status === AccountStatus.ACTIVE) {
        control.setValue(this.selectedAll);
      }
    });

    this.selectAllCheckboxForm.setValue(this.selectedAll);

    safelyDetectChanges(this.changeDetector);
  }

  public toggleUserCheckbox(index: number): void {
    const selectedItemForm = this.usersCheckboxFormArray[index];
    selectedItemForm.setValue(!selectedItemForm.value);

    this.selectedAll = this.usersCheckboxFormArray
      .filter((_, i) => this.users[i]?.account_status === AccountStatus.ACTIVE) // Only consider active users
      .every((control) => control.value === true);
    this.selectAllCheckboxForm.setValue(this.selectedAll);

    safelyDetectChanges(this.changeDetector);
  }

  public submit(): void {
    this.form.markAsDirty();
    if (this.form.status === 'VALID') {
      this.chatSendMessageToService.sendMessageToProfiles(this.extractSelectedUser(), true);
    }
  }

  private extractSelectedUser(): UserBasic[] {
    return this.form.value.items
      .map((item: boolean, index: number) =>
        item ? this.users.filter((user: UserBasic) => user.account_status === AccountStatus.ACTIVE).at(index) : null,
      )
      .filter(Boolean);
  }

  private createForm(): void {
    this.form = new FormGroup({
      selectAll: new FormControl(this.selectedAll),
      items: new FormArray(
        this.users.map(
          (user: UserBasic) =>
            new FormControl({ value: false, disabled: user.account_status !== AccountStatus.ACTIVE }),
        ),
        this.minSelected(),
      ),
    });

    safelyDetectChanges(this.changeDetector);
  }

  private minSelected(min = 1): ValidatorFn {
    return (control: AbstractControl<boolean[]>) => {
      if (control instanceof FormArray) {
        return control.getRawValue().filter(Boolean).length >= min ? null : { required: true };
      }

      return null;
    };
  }

  private pageParams(): Observable<PageParams> {
    const mapPageParams: (params: ParamMap) => PageParams = (params: ParamMap) => {
      const {
        sorting: defaultSorting,
        filtering: defaultFiltering,
        queryKeyword: defaultQuery,
      } = this.pageConfig.initialState;

      const rawSortingParam = params.get('sorting') ?? '';
      let effectiveSorting = toSortCategories(rawSortingParam || undefined, defaultSorting);

      if (!rawSortingParam && defaultQuery === '' && defaultSorting === SortCategories.SCORE_DESC) {
        effectiveSorting = SortCategories.NAME_ASC;
      }

      return {
        page: parseInt(params.get('page')) || 1,
        filtering: params.get('filtering') ?? defaultFiltering,
        sorting: effectiveSorting,
      };
    };

    return this.activatedRoute.queryParamMap.pipe(
      map(mapPageParams),
      tap((params: PageParams) => {
        this.relationRequestService.loadAcceptedRequest(
          {
            currentPage: params.page,
            totalItemsCount: 0,
          },
          params.sorting,
          params.filtering,
        );
      }),
    );
  }

  private get selectAllCheckboxForm(): AbstractControl {
    return this.form.get('selectAll');
  }

  private get usersCheckboxFormArray(): AbstractControl[] {
    return (this.form.get('items') as FormArray).controls;
  }
}
